import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar } from "../shared/Avatar";
import { CustomIcon } from "../shared/CustomIcon";
import { Card } from "../shared/Card";
import { Button } from "../shared/Button";
import { Badge } from "../shared/Badge";
import EmptyState from "../shared/EmptyState";
import { fullName, formatDate, uid } from "../../lib/format";
import MyAttendancePanel from "../dashboard/MyAttendancePanel";
import { expandEventOccurrences } from "../calendar/helpers";

// Combined dropdown value scheme: "svc:<id>" or "evt:<id>".
// This lets us tell whether the selection is a Service or an Event so we
// persist it on the right field on the attendance record (serviceId vs eventId).
const SVC_PREFIX = "svc:";
const EVT_PREFIX = "evt:";
const DAY_LABEL = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

// Smart pre-selection window (in ms): user is likely to record attendance
// starting up to 2h before a service and up to 3h after the start time.
const WINDOW_BEFORE_MS = 2 * 60 * 60 * 1000;
const WINDOW_AFTER_MS  = 3 * 60 * 60 * 1000;
// Fallback search horizon — most recent past occurrence within this many days.
const FALLBACK_MAX_DAYS_BACK = 400;

// --- Smart pre-selection helpers -----------------------------------------
function ymdKey(d) {
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${yr}-${mo}-${da}`;
}

// Parse an HH:MM string into { h, m } or null.
function parseHM(t) {
  if (!t) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(String(t));
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return { h, m: min };
}

// Combine a local Date (day) with an HH:MM string into a local Date-time.
// If time is missing/invalid, anchors to noon so the +/- window still covers
// most of the day without wrapping into neighbouring days.
function combineDateTime(day, timeStr) {
  const hm = parseHM(timeStr);
  const d = new Date(day.getFullYear(), day.getMonth(), day.getDate());
  if (hm) d.setHours(hm.h, hm.m, 0, 0);
  else d.setHours(12, 0, 0, 0);
  return d;
}

// Check if a specific day is an occurrence of a service (kind="svc") or
// event (kind="evt"). Honors recurrence exceptions on both.
function isOccurrenceOn(entity, kind, day) {
  if (!entity) return false;
  const isoDay = ymdKey(day);
  if (kind === "svc") {
    const rec = entity.recurrence || {};
    if (Array.isArray(rec.exceptions) && rec.exceptions.includes(isoDay)) return false;
    if (entity.type === "one-time" || entity.type === "other") {
      return String(entity.eventDate || "").slice(0, 10) === isoDay;
    }
    // Recurring services: match on dayOfWeek (0-6). recurrence.until caps future/past.
    if (typeof entity.dayOfWeek !== "number") return false;
    if (day.getDay() !== entity.dayOfWeek) return false;
    if (rec.until) {
      const untilMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(rec.until));
      if (untilMatch) {
        const untilD = new Date(Number(untilMatch[1]), Number(untilMatch[2]) - 1, Number(untilMatch[3]));
        if (day > untilD) return false;
      }
    }
    return true;
  }
  // Event — reuse the calendar helper's full recurrence expansion.
  const occ = expandEventOccurrences(entity, day, day);
  return occ.length > 0;
}

// Walk backwards day-by-day to find the most recent past occurrence date.
// Returns a Date or null.
function findMostRecentPastOccurrence(entity, kind, now, maxDaysBack = FALLBACK_MAX_DAYS_BACK) {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  for (let i = 0; i <= maxDaysBack; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() - i);
    if (isOccurrenceOn(entity, kind, d)) return d;
  }
  return null;
}

function buildOptionLabel(entity, kind) {
  if (kind === "svc") {
    if (entity.type === "recurring" && typeof entity.dayOfWeek === "number") {
      return `${entity.name} — Every ${DAY_LABEL[entity.dayOfWeek] || "Sunday"}`;
    }
    if (entity.eventDate) return `${entity.name} — ${formatDate(entity.eventDate)}`;
    return entity.name;
  }
  return entity.date ? `${entity.name} — ${formatDate(entity.date)}` : entity.name;
}

function AttendancePage({ currentUser, isSuperAdmin, isPastor, isTeamMember, services, events, attendance, setAttendance, members, visibleMembers, addAudit, addNotification, persist }) {
  const navigate = useNavigate();
  // Server-side enforced; this mirrors the same rule client-side so the UI
  // hides marking controls when the user is read-only.
  const canMark = (
    currentUser.role === "superadmin" ||
    currentUser.role === "limited_admin" ||
    currentUser.role === "pastor" ||
    (currentUser.role === "team_member" && currentUser.attendanceAccess === true)
  );
  const sortedServices = useMemo(
    () => [...(services || [])].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [services]
  );
  const sortedEvents = useMemo(
    () => [...(events || [])].sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0)),
    [events]
  );

  // Default to the first service if available, else the first event.
  const firstValue = useMemo(() => {
    if (sortedServices[0]) return `${SVC_PREFIX}${sortedServices[0].id}`;
    if (sortedEvents[0])   return `${EVT_PREFIX}${sortedEvents[0].id}`;
    return "";
  }, [sortedServices, sortedEvents]);

  const [selectedKey, setSelectedKey] = useState(firstValue);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  // Smart pre-selection: "window" = match inside +/- hours of a service today,
  // "fallback" = most recent past occurrence; null = manual or no suggestion.
  const [autoBadge, setAutoBadge] = useState(null);
  // Once the user changes the dropdown or the date, we stop auto-selecting so
  // we never fight their manual pick.
  const userTouchedRef = useRef(false);
  const didAutoSelectRef = useRef(false);
  const [tab, setTab] = useState("mark");
  const [memberSearch, setMemberSearch] = useState("");
  // Super-Admin-only deletion confirm. Holds the record pending delete.
  const [pendingDelete, setPendingDelete] = useState(null);
  // Trash page — permanent delete confirm.
  const [pendingPermDelete, setPendingPermDelete] = useState(null);
  const myMembers = canMark ? (members || []) : visibleMembers(currentUser);
  const filteredMembers = useMemo(() => {
    const q = memberSearch.trim().toLowerCase();
    if (!q) return myMembers;
    return myMembers.filter(m => fullName(m).toLowerCase().includes(q));
  }, [myMembers, memberSearch]);

  // Smart pre-selection — runs once when services/events first arrive (or on
  // mount if they're already loaded). Auto-picks the most likely service+date
  // based on the current time. User interaction disables further auto-runs.
  //
  //   1. Window match: For each service/event with a time that occurs TODAY,
  //      pre-select it if `now` is within [start - 2h, start + 3h].
  //      Tiebreaker: smallest |now - start|. Shows a gold "Auto-selected" badge.
  //   2. Fallback: Otherwise, pick the most recently occurred service/event
  //      (past instance closest to now) and set the date to that occurrence.
  //      Shows a subtle gold notice explaining the choice.
  //
  // Recover-if-empty: also handles the case where services + events loaded
  // async after first render — the first non-empty pass triggers auto-select.
  useEffect(() => {
    if (didAutoSelectRef.current) return;
    if (userTouchedRef.current) return;
    if (sortedServices.length === 0 && sortedEvents.length === 0) return;
    didAutoSelectRef.current = true;

    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const todayIso = ymdKey(todayStart);

    // 1) Window match — services/events with a start time that occur today
    const windowCandidates = [];
    for (const s of sortedServices) {
      if (!s.time) continue;
      if (!isOccurrenceOn(s, "svc", todayStart)) continue;
      const start = combineDateTime(todayStart, s.time);
      const diff = now - start;
      if (diff >= -WINDOW_BEFORE_MS && diff <= WINDOW_AFTER_MS) {
        windowCandidates.push({ kind: "svc", id: s.id, absDiff: Math.abs(diff) });
      }
    }
    for (const ev of sortedEvents) {
      if (!ev.time) continue;
      if (!isOccurrenceOn(ev, "evt", todayStart)) continue;
      const start = combineDateTime(todayStart, ev.time);
      const diff = now - start;
      if (diff >= -WINDOW_BEFORE_MS && diff <= WINDOW_AFTER_MS) {
        windowCandidates.push({ kind: "evt", id: ev.id, absDiff: Math.abs(diff) });
      }
    }
    if (windowCandidates.length > 0) {
      windowCandidates.sort((a, b) => a.absDiff - b.absDiff);
      const w = windowCandidates[0];
      setSelectedKey((w.kind === "svc" ? SVC_PREFIX : EVT_PREFIX) + w.id);
      setSelectedDate(todayIso);
      setAutoBadge("window");
      return;
    }

    // 2) Fallback — most recent past occurrence across all services/events
    let best = null; // { kind, id, date: Date }
    for (const s of sortedServices) {
      const d = findMostRecentPastOccurrence(s, "svc", now);
      if (!d) continue;
      if (!best || d > best.date) best = { kind: "svc", id: s.id, date: d };
    }
    for (const ev of sortedEvents) {
      const d = findMostRecentPastOccurrence(ev, "evt", now);
      if (!d) continue;
      if (!best || d > best.date) best = { kind: "evt", id: ev.id, date: d };
    }
    if (best) {
      setSelectedKey((best.kind === "svc" ? SVC_PREFIX : EVT_PREFIX) + best.id);
      setSelectedDate(ymdKey(best.date));
      setAutoBadge("fallback");
      return;
    }

    // Nothing to auto-pick — keep the existing default (first item / empty).
    if (!selectedKey && firstValue) setSelectedKey(firstValue);
  }, [sortedServices, sortedEvents, firstValue, selectedKey]);

  const { kind: selectedKind, id: selectedId } = useMemo(() => {
    if (selectedKey && selectedKey.startsWith(SVC_PREFIX)) return { kind: "svc", id: selectedKey.slice(SVC_PREFIX.length) };
    if (selectedKey && selectedKey.startsWith(EVT_PREFIX)) return { kind: "evt", id: selectedKey.slice(EVT_PREFIX.length) };
    return { kind: "svc", id: selectedKey || "" };
  }, [selectedKey]);

  const selectedEntity = useMemo(() => (
    selectedKind === "evt"
      ? (events || []).find(e => e.id === selectedId)
      : (services || []).find(s => s.id === selectedId)
  ), [selectedKind, selectedId, services, events]);

  // History lookup matches existing records on either serviceId (services) or
  // eventId (events) so we never collide between them.
  const existingRecord = useMemo(() => (
    attendance.find(a => (
      a.date === selectedDate &&
      (selectedKind === "evt"
        ? a.eventId === selectedId
        : a.serviceId === selectedId)
    ))
  ), [attendance, selectedDate, selectedKind, selectedId]);

  const [records, setRecords] = useState({});
  const [reasons, setReasons] = useState({});
  const sortedAttendance = useMemo(
    () => [...attendance].filter(a => !a.deletedAt).sort((a, b) => new Date(b.date) - new Date(a.date)),
    [attendance]
  );
  const trashedAttendance = useMemo(
    () => [...attendance].filter(a => !!a.deletedAt).sort((a, b) => new Date(b.deletedAt) - new Date(a.deletedAt)),
    [attendance]
  );

  useEffect(() => {
    if (existingRecord) { setRecords(existingRecord.records || {}); setReasons(existingRecord.absenceReasons || {}); }
    else { setRecords({}); setReasons({}); }
  }, [existingRecord]);

  const markAttendance = (memberId, status) => {
    setRecords(r => ({ ...r, [memberId]: status }));
    if (status === "present") setReasons(r => { const n = { ...r }; delete n[memberId]; return n; });
  };

  const saveAttendance = () => {
    if (!canMark) { alert("You don't have permission to record attendance. Ask a Super Admin to enable Attendance Access on your profile."); return; }
    if (!selectedEntity) { alert("Pick a service or event first."); return; }
    // Auto-fill: any member in the roster who wasn't explicitly marked
    // "present" is recorded as "absent" so every member ends up with a
    // definite status. Existing "absent" marks (with optional reasons) are
    // preserved untouched.
    const finalRecords = { ...records };
    (myMembers || []).forEach(m => {
      if (finalRecords[m.id] !== "present") finalRecords[m.id] = "absent";
    });
    const base = { id: existingRecord?.id || uid(), date: selectedDate, records: finalRecords, absenceReasons: reasons };
    const data = selectedKind === "evt"
      ? { ...base, eventId: selectedId,  serviceId: existingRecord?.serviceId || null }
      : { ...base, serviceId: selectedId, eventId: existingRecord?.eventId || null };
    const updated = existingRecord ? attendance.map(a => a.id === existingRecord.id ? data : a) : [...attendance, data];
    setAttendance(updated); persist("kc_attendance", updated);
    const label = selectedEntity.name;
    addAudit(currentUser.id, currentUser.name, "Marked attendance", `${label} — ${formatDate(selectedDate)}`);
    addNotification(`Attendance recorded for ${label} on ${formatDate(selectedDate)}`, "attendance", { ...(selectedKind === "evt" ? { eventId: selectedId } : { serviceId: selectedId }), date: selectedDate });
    alert("Attendance saved!");
  };

  const presentCount = Object.values(records).filter(v => v === "present").length;
  const absentCount  = Object.values(records).filter(v => v === "absent").length;

  // History row lookup: show "Service / Event" name regardless of which type
  // the record was logged against. Falls back to "Unknown" if both lookups fail.
  const labelForRecord = (a) => {
    if (a.eventId) {
      const e = (events || []).find(x => x.id === a.eventId);
      return e ? e.name : "Unknown";
    }
    const s = (services || []).find(x => x.id === a.serviceId);
    return s ? s.name : "Unknown";
  };

  return (
    <div>
      {/* Gold optgroup styling — Cinzel uppercase with a subtle divider line under each header. */}
      <style>{`
        .attendance-dropdown {
          font-family: 'Inter', sans-serif;
        }
        .attendance-dropdown optgroup {
          font-family: 'Cinzel', serif;
          font-weight: 700;
          font-size: 12px;
          letter-spacing: 0.10em;
          text-transform: uppercase;
          color: var(--kc-accent);
          background: var(--kc-surface);
        }
        .attendance-dropdown option {
          font-family: 'Inter', sans-serif;
          font-weight: 400;
          color: var(--kc-text);
          background: var(--kc-surface);
          letter-spacing: normal;
          text-transform: none;
        }
        /* Per-member Present/Absent toggle — never primary. Uses the secondary
           outline look with semantic colours when active. */
        .att-status-toggle {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 6px 12px;
          border-radius: var(--kc-radius-xs);
          font-family: 'Inter', sans-serif;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.6px;
          text-transform: uppercase;
          background: var(--kc-elevated);
          color: var(--kc-sub);
          border: 1.5px solid var(--kc-border);
          cursor: pointer;
          transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
        }
        .att-status-toggle:hover { color: var(--kc-text); border-color: var(--kc-sub); }
        .att-status-toggle[data-status="present"][data-active="true"] {
          background: var(--kc-green-bg);
          color: var(--kc-green);
          border-color: var(--kc-green);
        }
        .att-status-toggle[data-status="absent"][data-active="true"] {
          background: var(--kc-red-bg);
          color: var(--kc-red);
          border-color: var(--kc-red);
        }
      `}</style>
      <div className="tabs">
        <button className={`tab ${tab === "mark" ? "active" : ""}`} onClick={() => setTab("mark")}>Mark Attendance</button>
        <button className={`tab ${tab === "myatt" ? "active" : ""}`} data-testid="att-tab-my" onClick={() => setTab("myatt")}>My Attendance</button>
        <button className={`tab ${tab === "history" ? "active" : ""}`} onClick={() => setTab("history")}>History</button>
        {isSuperAdmin && (
          <button
            className={`tab ${tab === "trash" ? "active" : ""}`}
            data-testid="att-tab-trash"
            onClick={() => setTab("trash")}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          ><CustomIcon name="trash" size={13} /> Trash{trashedAttendance.length > 0 ? ` (${trashedAttendance.length})` : ""}</button>
        )}
      </div>
      {tab === "mark" && (
        <div>
          <Card style={{ padding: 18, marginBottom: 18 }}>
            <div className="flex gap-16 flex-wrap items-center">
              <div className="form-group" style={{ flex: 1, minWidth: 240 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span>Services / Events</span>
                  {autoBadge === "window" && (
                    <Badge
                      variant="blue"
                      data-testid="att-auto-selected-badge"
                      title="Auto-selected based on the current time"
                      style={{ gap: 4 }}
                    >
                      <CustomIcon name="sparkle" size={10} /> Auto-selected
                    </Badge>
                  )}
                </label>
                <select
                  className="attendance-dropdown"
                  data-testid="att-service-event-select"
                  value={selectedKey}
                  onChange={e => {
                    userTouchedRef.current = true;
                    setAutoBadge(null);
                    setSelectedKey(e.target.value);
                  }}
                >
                  {sortedServices.length === 0 && sortedEvents.length === 0 && (
                    <option value="" disabled>No services or events yet</option>
                  )}
                  {sortedServices.length > 0 && (
                    <optgroup label="── SERVICES ──" data-testid="att-optgroup-services">
                      {sortedServices.map(s => (
                        <option key={s.id} value={`${SVC_PREFIX}${s.id}`} data-testid={`att-opt-svc-${s.id}`}>
                          {buildOptionLabel(s, "svc")}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {sortedEvents.length > 0 && (
                    <optgroup label="── EVENTS ──" data-testid="att-optgroup-events">
                      {sortedEvents.map(e => (
                        <option key={e.id} value={`${EVT_PREFIX}${e.id}`} data-testid={`att-opt-evt-${e.id}`}>
                          {buildOptionLabel(e, "evt")}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>
              <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
                <label>Date</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={e => {
                    userTouchedRef.current = true;
                    setAutoBadge(null);
                    setSelectedDate(e.target.value);
                  }}
                />
              </div>
              <div style={{ marginTop: 20, display: "flex", gap: 8 }}>
                <Badge variant="green" style={{ gap: 4 }}><CustomIcon name="check" size={12} /> {presentCount} present</Badge>
                <Badge variant="red" style={{ gap: 4 }}><CustomIcon name="close" size={12} /> {absentCount} absent</Badge>
              </div>
            </div>
            {autoBadge === "fallback" && (
              <div
                data-testid="att-auto-fallback-notice"
                style={{
                  marginTop: 14,
                  background: "var(--kc-accent-bg)",
                  border: "1px solid color-mix(in srgb, var(--kc-accent) 35%, transparent)",
                  borderRadius: "var(--kc-radius-sm)",
                  padding: "10px 14px",
                  color: "var(--kc-accent)",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
                className="text-card-secondary"
              >
                <CustomIcon name="sparkle" size={14} />
                <span>Suggested based on the most recent service — tap to change if needed.</span>
              </div>
            )}
          </Card>
          <Card style={{ padding: 0 }}>
            <div className="card-header">
              <div className="card-title">{canMark ? "Mark Attendance" : "View Attendance (Read-Only)"}</div>
              <div className="flex gap-8">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => window.print()}
                  data-testid="att-print-sheet"
                >
                  <CustomIcon name="printer" size={13} /> Print Sheet
                </Button>
                {canMark && (
                  <Button
                    variant="secondary"
                    size="sm"
                    data-testid="att-mark-all-present"
                    onClick={() => { const r = {}; myMembers.forEach(m => r[m.id] = "present"); setRecords(r); }}
                  >
                    Mark All Present
                  </Button>
                )}
                {canMark && (
                  <Button
                    variant="primary"
                    size="sm"
                    data-testid="att-save-btn"
                    onClick={saveAttendance}
                  >
                    Save
                  </Button>
                )}
              </div>
            </div>

            {!canMark && (
              <div
                data-testid="att-readonly-banner"
                className="text-card-secondary"
                style={{
                  margin: "0 20px 14px",
                  background: "var(--kc-accent-bg)",
                  border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)",
                  color: "var(--kc-text)",
                  borderRadius: "var(--kc-radius-sm)",
                  padding: "10px 14px",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <span style={{ display: "inline-flex" }}><CustomIcon name="lock" size={16} /></span>
                <span>You&apos;re viewing in read-only mode. Ask a Super Admin to enable <strong>Attendance Access</strong> on your profile to record attendance.</span>
              </div>
            )}

            {/* Member search bar — filters the list in real time */}
            <div style={{ padding: "0 20px 14px" }}>
              <div className="att-search-wrap" data-testid="att-member-search-wrap" style={{ position: "relative" }}>
                <span aria-hidden="true" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", display: "inline-flex" }}><CustomIcon name="search" size={14} /></span>
                <input
                  type="search"
                  data-testid="att-member-search"
                  className="att-member-search-input"
                  value={memberSearch}
                  onChange={e => setMemberSearch(e.target.value)}
                  placeholder="Search members..."
                  style={{
                    width: "100%",
                    background: "var(--kc-elevated)",
                    border: "1.5px solid var(--kc-border)",
                    color: "var(--kc-text)",
                    borderRadius: "var(--kc-radius-sm)",
                    padding: "10px 12px 10px 36px",
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 13,
                    outline: "none",
                    transition: "border-color 0.15s, background 0.15s",
                  }}
                  onFocus={e => { e.currentTarget.style.borderColor = "var(--kc-accent)"; }}
                  onBlur={e => { e.currentTarget.style.borderColor = "var(--kc-border)"; }}
                />
              </div>
              {memberSearch && filteredMembers.length === 0 && (
                <div
                  data-testid="att-search-empty"
                  className="text-card-secondary"
                  style={{ marginTop: 10, padding: 16, textAlign: "center" }}
                >
                  No members match &ldquo;{memberSearch}&rdquo;.
                </div>
              )}
            </div>

            <div style={{ padding: "0 20px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
              {filteredMembers.map(m => {
                const status = records[m.id];
                const rowVariant = status === "present" ? "green" : status === "absent" ? "red" : "none";
                return (
                  <Card
                    key={m.id}
                    variant={rowVariant}
                    data-testid={`att-row-${m.id}`}
                    style={{
                      padding: "12px 14px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 10,
                      flexWrap: "wrap",
                    }}
                  >
                    <div className="flex items-center gap-10">
                      <Avatar name={fullName(m)} photo={m.photo} memberId={m.id} status={m.memberStatus} style={{ fontSize: 11 }} />
                      <div>
                        <div className="text-card-primary">{fullName(m)}</div>
                        <div className="text-meta">{m.memberStatus}</div>
                      </div>
                    </div>
                    {canMark ? (
                      <div className="flex gap-8 items-center" style={{ flexWrap: "wrap" }}>
                        <button
                          type="button"
                          data-testid={`att-present-${m.id}`}
                          onClick={() => markAttendance(m.id, "present")}
                          className="att-status-toggle"
                          data-active={status === "present"}
                          data-status="present"
                        >
                          <CustomIcon name="check" size={12} /> Present
                        </button>
                        <button
                          type="button"
                          data-testid={`att-absent-${m.id}`}
                          onClick={() => markAttendance(m.id, "absent")}
                          className="att-status-toggle"
                          data-active={status === "absent"}
                          data-status="absent"
                        >
                          <CustomIcon name="close" size={12} /> Absent
                        </button>
                        {status === "absent" && (
                          <select
                            value={reasons[m.id] || ""}
                            onChange={e => setReasons(r => ({ ...r, [m.id]: e.target.value }))}
                            style={{
                              fontSize: 12,
                              padding: "6px 10px",
                              borderRadius: "var(--kc-radius-xs)",
                              fontFamily: "'Inter',sans-serif",
                              background: "var(--kc-elevated)",
                              border: "1px solid var(--kc-border)",
                              color: "var(--kc-text)",
                            }}
                          >
                            <option value="">Reason...</option>
                            <option>Sick</option><option>Travel</option><option>Work</option>
                            <option>No reason given</option><option>Family emergency</option><option>Other</option>
                          </select>
                        )}
                      </div>
                    ) : (
                      <div className="flex gap-8 items-center">
                        {status === "present" ? (
                          <Badge variant="green" data-testid={`att-readonly-${m.id}`} style={{ gap: 4 }}>
                            <CustomIcon name="check" size={12} /> Present
                          </Badge>
                        ) : status === "absent" ? (
                          <Badge variant="red" data-testid={`att-readonly-${m.id}`} style={{ gap: 4 }}>
                            <CustomIcon name="close" size={12} /> Absent
                          </Badge>
                        ) : (
                          <Badge variant="grey" data-testid={`att-readonly-${m.id}`}>— Not marked</Badge>
                        )}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          </Card>
        </div>
      )}
      {tab === "myatt" && (
        <Card data-testid="att-my-tab" style={{ padding: 20 }}>
          <MyAttendancePanel
            currentUser={currentUser}
            members={members}
            attendance={attendance}
            services={services}
            events={events}
            isPastor={isPastor}
            isTeamMember={isTeamMember}
            navigate={navigate}
            defaultPeriod="month"
          />
        </Card>
      )}
      {tab === "history" && (
        <Card style={{ padding: 0 }}>
          <div className="card-header"><div className="card-title">Attendance History</div></div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Date</th><th>Service / Event</th><th>Present</th><th>Absent</th><th>Rate</th>{isSuperAdmin && <th style={{ width: 110 }}>Actions</th>}</tr></thead>
              <tbody>
                {sortedAttendance.map(a => {
                  const label = labelForRecord(a);
                  const present = Object.values(a.records).filter(v => v === "present").length;
                  const absent  = Object.values(a.records).filter(v => v === "absent").length;
                  const total = present + absent;
                  const rate = total ? Math.round(present / total * 100) : 0;
                  return (
                    <tr key={a.id} data-testid={`att-history-row-${a.id}`}>
                      <td>{formatDate(a.date)}</td>
                      <td>
                        {label}
                        {a.eventId ? <Badge variant="blue" style={{ marginLeft: 8 }}>Event</Badge> : null}
                      </td>
                      <td><Badge variant="green">{present}</Badge></td>
                      <td><Badge variant="red">{absent}</Badge></td>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ flex: 1, height: 6, background: "var(--kc-elevated)", borderRadius: 3, overflow: "hidden", minWidth: 70 }}>
                            <div style={{ width: `${rate}%`, height: "100%", background: rate > 70 ? "var(--kc-green)" : "var(--kc-amber)", borderRadius: 3 }} />
                          </div>
                          <span className="text-meta">{rate}%</span>
                        </div>
                      </td>
                      {isSuperAdmin && (
                        <td>
                          <Button
                            variant="destructive"
                            data-testid={`att-history-delete-${a.id}`}
                            onClick={() => setPendingDelete({ id: a.id, label, date: a.date })}
                          >Delete</Button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {tab === "trash" && isSuperAdmin && (
        <Card data-testid="att-trash-page" style={{ padding: 0 }}>
          <div className="card-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div className="card-title" style={{ display: "flex", alignItems: "center", gap: 8 }}><CustomIcon name="trash" size={16} /> Attendance Trash</div>
            <span className="text-eyebrow">
              Records auto-purge after 30 days
            </span>
          </div>
          <div style={{ padding: 18 }}>
            {trashedAttendance.length === 0 ? (
              <EmptyState
                data-testid="att-trash-empty"
                icon="trash"
                title="Nothing in Trash"
                subtitle="Soft-deleted attendance records will appear here for up to 30 days."
              />
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {trashedAttendance.map(a => {
                  const label = labelForRecord(a);
                  const present = Object.values(a.records || {}).filter(v => v === "present").length;
                  const absent  = Object.values(a.records || {}).filter(v => v === "absent").length;
                  return (
                    <Card
                      key={a.id}
                      variant="red"
                      data-testid={`att-trash-row-${a.id}`}
                      style={{ padding: 16 }}
                    >
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                        <div style={{ color: "var(--kc-red)", lineHeight: 1, display: "inline-flex", flexShrink: 0 }}>
                          <CustomIcon name="trash" size={26} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="text-section-header" style={{ marginBottom: 4 }}>
                            {label}
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }} className="text-card-secondary">
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><CustomIcon name="calendar" size={12} /> Original date: <strong>{formatDate(a.date)}</strong></span>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><CustomIcon name="check" size={12} /> Present {present}</span>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><CustomIcon name="close" size={12} /> Absent {absent}</span>
                          </div>
                          <div className="text-meta" style={{ marginTop: 6 }}>
                            Deleted by <strong style={{ color: "var(--kc-sub)" }}>{a.deletedBy || "Unknown"}</strong>
                            {" "}on <strong style={{ color: "var(--kc-sub)" }}>{a.deletedAt ? new Date(a.deletedAt).toLocaleString() : "—"}</strong>
                          </div>
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
                        <Button
                          variant="secondary"
                          size="sm"
                          data-testid={`att-trash-restore-${a.id}`}
                          onClick={() => {
                            const updated = (attendance || []).map(x => {
                              if (x.id !== a.id) return x;
                              const { deletedAt, deletedBy, deletedById, ...rest } = x;
                              return rest;
                            });
                            setAttendance(updated);
                            persist("kc_attendance", updated);
                            addAudit && addAudit(
                              currentUser.id,
                              currentUser.name,
                              "Restored attendance record from Trash",
                              `${label} on ${formatDate(a.date)}`,
                            );
                          }}
                        >↩ Restore</Button>
                        <Button
                          variant="destructive"
                          data-testid={`att-trash-perm-delete-${a.id}`}
                          onClick={() => setPendingPermDelete({ id: a.id, label, date: a.date })}
                        >Delete Permanently</Button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Permanent delete confirmation (from Trash) */}
      {pendingPermDelete && isSuperAdmin && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setPendingPermDelete(null)}>
          <div className="modal" data-testid="att-perm-delete-confirm" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Permanently Delete Record</div>
              <button className="modal-close" onClick={() => setPendingPermDelete(null)}>×</button>
            </div>
            <div className="modal-body">
              <p className="text-card-secondary" data-testid="att-perm-delete-text" style={{ lineHeight: 1.55 }}>
                Permanently delete this record? This cannot be undone, even from Trash.
              </p>
            </div>
            <div className="modal-footer">
              <Button variant="tertiary" data-testid="att-perm-delete-cancel" onClick={() => setPendingPermDelete(null)}>Cancel</Button>
              <Button
                variant="destructive"
                data-testid="att-perm-delete-confirm-btn"
                onClick={() => {
                  const target = pendingPermDelete;
                  const updated = (attendance || []).filter(x => x.id !== target.id);
                  setAttendance(updated);
                  persist("kc_attendance", updated);
                  addAudit && addAudit(
                    currentUser.id,
                    currentUser.name,
                    "Permanently deleted attendance record from Trash",
                    `${target.label} on ${formatDate(target.date)}`,
                  );
                  setPendingPermDelete(null);
                }}
              >Confirm Permanent Delete</Button>
            </div>
          </div>
        </div>
      )}


      {/* Super-Admin-only delete confirmation. Lives outside the History card
          so it overlays the page from anywhere on the screen. */}
      {pendingDelete && isSuperAdmin && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setPendingDelete(null)}>
          <div className="modal" data-testid="att-delete-confirm" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Delete Attendance Record</div>
              <button className="modal-close" onClick={() => setPendingDelete(null)}>×</button>
            </div>
            <div className="modal-body">
              <p className="text-card-secondary" data-testid="att-delete-confirm-text" style={{ lineHeight: 1.55 }}>
                Delete this attendance record for <strong>{pendingDelete.label}</strong> on <strong>{formatDate(pendingDelete.date)}</strong>? This cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <Button variant="tertiary" data-testid="att-delete-cancel" onClick={() => setPendingDelete(null)}>Cancel</Button>
              <Button
                variant="destructive"
                data-testid="att-delete-confirm-btn"
                onClick={() => {
                  const target = pendingDelete;
                  // Soft delete: stamp deletedAt + deletedBy. Record stays in
                  // storage but is filtered out of sortedAttendance and shown
                  // on the SA-only Trash tab where it can be restored or
                  // permanently deleted within 30 days.
                  const stamp = new Date().toISOString();
                  const updated = (attendance || []).map(x =>
                    x.id === target.id
                      ? { ...x, deletedAt: stamp, deletedBy: currentUser.name, deletedById: currentUser.id }
                      : x
                  );
                  setAttendance(updated);
                  persist("kc_attendance", updated);
                  addAudit && addAudit(
                    currentUser.id,
                    currentUser.name,
                    "Deleted attendance record",
                    `${target.label} on ${formatDate(target.date)}`,
                  );
                  setPendingDelete(null);
                }}
              >Confirm Delete</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export { AttendancePage };
